<template>
  <v-container fluid>
    <PageTitle :title="pageTitle" :counter_count="activeUsersCount" counter_label="Active" />
    <div class="mt-3" style="border: 1px solid #e5e5e5; border-radius: 8px">
      <ExBreadcrumbs class="mt-2 ml-1" :breadcrumbsList="dynamicBreadcrumbs" />
      <div class="account-header">
        <img
          class="account-header-avatar"
          :src="require('@components/assets/icons/account_icon.svg')"
          alt=""
        />
        <div class="account-header-main">
          <div class="account-header-title-row">
            <h2 class="account-header-name">{{ selectedAccount.account }}</h2>
            <AccountStatusBadge :status="selectedAccount.status" class="account-header-status" />
          </div>
          <p class="account-header-email">{{ selectedAccount.email }}</p>
        </div>
      </div>
      <HorizontalMenu
        class="px-3"
        :items="menuItems"
        size="small"
        :hideLines="true"
        :value="selectedTab"
        @onChange="handleTabChange"
      />
      <UniversalTable
        v-if="selectedTab === 0"
        class="user-tr ex-admin-account-access-table"
        :config="customTableConfig"
        :items="sortedUsers"
        :itemsPerPage="accessPageSize"
        :backendPagination="true"
        :itemsCount="accessTotalCount"
        :externalCurrentPage="accessCurrentPage"
        :loading="accessLoading"
        :no-hover-effect="true"
        @onActionClick="viewState.state.addPayRangeModal.show = true"
        @onSearch="onAccessSearch"
        @onRequest="onAccessTableRequest"
        @onClick="viewState.state.filters.showFilterDialog = true"
        @removeFilter="viewState.removeFilters"
        :emptyObjectData="emptyObjectData"
        :breadcrumItems="dynamicBreadcrumbs"
      >
        <template #Dropdown-activator>
          <div class="icon-container">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="icon-svg"
            >
              <path
                d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                stroke="#344054"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </template>
      </UniversalTable>
      <AccountProductsTab
        v-else-if="selectedTab === 1 && !isUserDrilldown"
        :account-id="accountIdForApi"
      />
      <AccountAdoptionTab
        v-else-if="selectedTab === 2 && showAdoptionTab"
        :account-id="accountIdForApi"
      />
    </div>
    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Employee"
      :filterGroups="viewState.state.filters.filterGroups"
      :statusOptions="viewState.state.filters.statusOptions"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />

    <OwnerShipModal
      :dialog="viewState.state.showOnwerShipDialog.show"
      @onAction="transferOwner($event)"
      @back="() => (viewState.state.showOnwerShipDialog.show = false)"
      :items="items"
    />

    <ExActionsPopUp
      v-if="userAccountActionDialogObject?.show ?? false"
      :headline="userAccountActionDialogObject?.title"
      :htmlInfo="userAccountActionDialogObject.description"
      :actionLabel="userAccountActionDialogObject?.actionText"
      :cancelLabel="userAccountActionDialogObject?.cancelText || ''"
      :value="userAccountActionDialogObject?.show ?? false"
      :primary="userAccountActionDialogObject.primary ?? false"
      :termination="userAccountActionDialogObject.termination ?? false"
      :destructive="userAccountActionDialogObject.destructive ?? false"
      :avatar="{
        picture: '',
        firstName: currentSelectedRow?.user?.first_name || '',
        lastName: currentSelectedRow?.user?.last_name || '',
      }"
      @onAction="onActionUserConfirmationDialog"
      @input="onCloseUserConfirmationDialog"
    />
  </v-container>
</template>

<script setup>
import { onMounted, getCurrentInstance, computed, watch, ref } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import { useTableConfig } from "./composables/useTableConfig";
import { useAccountViewState } from "./composables/useAccountViewState";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import PageTitle from "@components/common/PageTitle.vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import AccountStatusBadge from "@components/AccountStatusBadge/AccountStatusBadge.vue";
import { format } from "date-fns";
import OwnerShipModal from "@components/OwnerShipModal/OwnerShipModal.vue";
import {
  transferOwnerShip,
  userResendInvite,
  userStateUpdateSuspend,
  userStateUpdateGiveBack,
  getListofUser,
  normalizeAccessListPayload,
  startImpersonation,
} from "@/api";
import configService from "@/services/config";
import { resolveAccountAppUrl } from "@components/utils/impersonationSession";
import {
  LD_FLAG_IDEA_MDP_276_IMPERSONATION,
  LD_FLAG_IDEA_MDP_627,
} from "@components/constants/launchDarklyFlagKeys";
import { impersonationFeatureEnabled } from "@/composables/dropdown/useDropdownItems";
import { isAdminSysadmin } from "@/utils/isAdminSysadmin";
import ExActionsPopUp from "@components/common/ExActionsPopUp.vue";
import AccountProductsTab from "./AccountProductsTab.vue";
import AccountAdoptionTab from "./adoption/AccountAdoptionTab.vue";
const app = getCurrentInstance();
const { proxy } = app;

const tableConfig = useTableConfig();
const viewState = useAccountViewState();

/** Dedicated route: `/accounts/:accountId/users/:userId` (name: UserAccountAccessView). */
const isUserDrilldown = computed(() => proxy.$route.name === "UserAccountAccessView");

const emptyObjectData = computed(() => ({
  singular: isUserDrilldown.value ? "User" : "Account",
  plural: isUserDrilldown.value ? "Users" : "Accounts",
}));

// Dynamic page title based on source
const pageTitle = computed(() => {
  return isUserDrilldown.value ? "Users" : "Accounts";
});

// Dynamic breadcrumbs based on source
const dynamicBreadcrumbs = computed(() => {
  if (isUserDrilldown.value) {
    return [
      {
        text: "Users",
        disabled: false,
        href: "/users",
      },
      {
        text: "User",
        disabled: true,
      },
    ];
  } else {
    return [
      {
        text: "Accounts",
        disabled: false,
        href: "/accounts",
      },
      {
        text: "Account",
        disabled: true,
      },
    ];
  }
});

// Count only users that have access or are active
const activeUsersCount = computed(() => {
  return users.value.filter((user) => {
    // Count only operational users: have access, not suspended, and not closed/blocked
    const state = user.user?.state;
    return user.has_access && !user.is_suspended && state !== "closed" && state !== "blocked";
  }).length;
});

const appAccessTypes = ref([
  {
    key: "has_readiness_access",
    permissionType: "App Permission",
    appDropdown: "Proficiency",
    IsEnabled: true,
  },
  {
    key: "has_knowledge_access",
    permissionType: "App Permission",
    appDropdown: "Knowledge",
    IsEnabled: true,
  },
  {
    key: "has_performance_access",
    permissionType: "App Permission",
    appDropdown: "Performance",
    IsEnabled: true,
  },
  {
    key: "has_compensation_access",
    permissionType: "App Permission",
    appDropdown: "Compensation",
    IsEnabled: true,
  },
]);

let selectedTab = ref(0);

/** Adoption tab (MDP-627 / ER-12306): sysadmin-only, fail-closed behind idea-MDP-627. */
const adoptionEnabled = ref(false);
const isSysadmin = ref(false);

const showAdoptionTab = computed(
  () => !isUserDrilldown.value && isSysadmin.value && adoptionEnabled.value
);

const users = ref([]);
const items = ref([]);
const selectedAccount = ref({});

/** Account id: `/accounts/:id` vs `/accounts/:accountId/users/:userId`. */
const accountIdForApi = computed(() => {
  if (isUserDrilldown.value) {
    return String(proxy.$route.params.accountId);
  }
  return String(proxy.$route.params.id);
});

/** User id for GET .../user/{user}/account/ (user drill-down route only). */
const userIdForAccessDrilldown = computed(() => {
  if (!isUserDrilldown.value) return null;
  return String(proxy.$route.params.userId);
});

// User actions state management
const currentSelectedRow = ref({});
const userActions = ref({
  showResendInvitePopup: false,
  showSuspendPopup: false,
  showGiveBackPopup: false,
  showImpersonatePopup: false,
});

/** Account id for suspend/resend/give-back: prefer row from GET user/{id}/account/ when present. */
const effectiveAccountIdForActions = computed(() => {
  const row = currentSelectedRow.value;
  if (row?.account != null && String(row.account).trim() !== "") {
    return String(row.account);
  }
  return accountIdForApi.value;
});

const accessPageSize = 10;
const accessTotalCount = ref(0);
const accessCurrentPage = ref(1);
const accessLoading = ref(false);
let accessSearchDebounceTimer = null;

const menuItems = computed(() => {
  const items = [{ title: "Access to Account", count: accessTotalCount.value }];
  if (!isUserDrilldown.value) {
    items.push({ title: "Products", count: null });
    if (showAdoptionTab.value) {
      items.push({ title: "Adoption", count: null });
    }
  }
  return items;
});

// User action dialog configuration
const userAccountActionDialogObject = computed(() => {
  if (userActions.value.showResendInvitePopup) {
    const firstName = currentSelectedRow.value?.user?.first_name || "";
    const lastName = currentSelectedRow.value?.user?.last_name || "";
    return {
      title: "Resend Invite",
      description: `You are about to resend invite to ${firstName} ${lastName}`,
      actionText: "Resend",
      primary: true,
      show: true,
      termination: false,
    };
  } else if (userActions.value.showSuspendPopup) {
    const firstName = currentSelectedRow.value?.user?.first_name || "";
    const lastName = currentSelectedRow.value?.user?.last_name || "";
    return {
      title: "Suspend Access",
      description: `You are about to suspend access for ${firstName} ${lastName}`,
      actionText: "Suspend",
      show: true,
      primary: false,
      termination: false,
      destructive: true,
    };
  } else if (userActions.value.showGiveBackPopup) {
    const firstName = currentSelectedRow.value?.user?.first_name || "";
    const lastName = currentSelectedRow.value?.user?.last_name || "";
    return {
      title: "Give back Access",
      description: `You are about to give back access for ${firstName} ${lastName}`,
      actionText: "Give back Access",
      show: true,
      primary: true,
      termination: false,
      destructive: false,
    };
  } else if (userActions.value.showImpersonatePopup) {
    const firstName = currentSelectedRow.value?.user?.first_name || "";
    const lastName = currentSelectedRow.value?.user?.last_name || "";
    return {
      title: "Are you sure you want to impersonate this user?",
      description: `You are about to impersonate ${firstName} ${lastName}. You will be able to view and interact with the application as this user. The impersonation will automatically end after 2 hours.`,
      actionText: "Yes",
      cancelText: "No, cancel",
      show: true,
      primary: true,
      termination: false,
      destructive: false,
    };
  }
  return {
    title: "",
    description: "",
    actionText: "",
    show: false,
    primary: false,
    termination: false,
  };
});

const handleTabChange = (tabIndex) => {
  localStorage.setItem("selectedTab", tabIndex);
  selectedTab.value = tabIndex;
};

/**
 * account_permissions[] from GET /v2/user/ listing — map to access rows for the table.
 */
const mapAccountPermissionToAccessRow = (perm, userIdStr, profile) => {
  const uid = Number(userIdStr);
  const fullName = (profile?.userName || profile?.account || "").trim();
  const parts = fullName.split(/\s+/);
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ") || "";
  const statusRaw = profile?.status ?? "operational";
  const state = String(statusRaw).toLowerCase();
  const ap = String(perm.admin_permission || "")
    .toLowerCase()
    .trim();

  return {
    id: perm.access_id ?? perm.id ?? Number(perm.account),
    account: perm.account,
    account_name: perm.account_name,
    admin_permission: perm.admin_permission,
    has_access: !!perm.has_access,
    is_suspended: !!perm.is_suspended,
    is_owner: ap === "owner",
    is_co_owner: ap === "co-owner",
    has_readiness_access: !!perm.has_readiness_access,
    has_knowledge_access: !!perm.has_knowledge_access,
    has_performance_access: !!perm.has_performance_access,
    has_compensation_access: !!perm.has_compensation_access,
    user: {
      id: profile?.id != null ? Number(profile.id) : uid,
      first_name: firstName,
      last_name: lastName,
      email: profile?.email ?? "",
      state,
      last_login: profile?.last_login ?? null,
    },
    has_employee: false,
  };
};

const mapUserRow = (item) => {
  const accessChip = [];
  const ap = String(item.admin_permission || "")
    .toLowerCase()
    .trim();

  // Role badges (Owner / Co-Owner / Administrator): show from API flags even when
  // has_access is false (e.g. suspended) so Admin Permissions is not a blank dash.
  if (item.is_owner || ap === "owner") {
    accessChip.push({
      permissionType: "Owner",
      appDropdown: "Owner",
      IsEnabled: true,
    });
  } else if (item.is_co_owner || ap === "co-owner") {
    accessChip.push({
      permissionType: "Co-Owner",
      appDropdown: "Co-Owner",
      IsEnabled: true,
    });
  } else if (ap === "administrator" || ap === "admin") {
    accessChip.push({
      permissionType: "Administrator",
      appDropdown: "Administrator",
      IsEnabled: true,
    });
  } else if (item.has_access) {
    appAccessTypes.value.forEach((access) => {
      if (item[access.key]) {
        accessChip.push(access);
      }
    });
  }

  return {
    ...item,
    accessChip,
    last_logged_in_at: item.user?.last_login
      ? format(new Date(item.user.last_login), "d-MMMM-yyyy HH:mm")
      : "-",
  };
};

const loadUsers = async (page = 1) => {
  accessLoading.value = true;
  try {
    const rawSearch = viewState.state.filters.searchText;
    const search =
      rawSearch && String(rawSearch).trim().length > 0 ? String(rawSearch).trim() : undefined;
    const uid = userIdForAccessDrilldown.value;
    const aid = accountIdForApi.value;

    if (uid != null) {
      const profile = selectedAccount.value || {};
      if (profile.id != null && String(profile.id) !== String(uid)) {
        users.value = [];
        accessTotalCount.value = 0;
        accessCurrentPage.value = 1;
        return;
      }
      const perms = Array.isArray(profile.account_permissions) ? profile.account_permissions : [];
      let rows = perms.map((p) => mapAccountPermissionToAccessRow(p, uid, profile));
      if (search) {
        const q = search.toLowerCase();
        rows = rows.filter((r) => {
          const name = `${r.user?.first_name || ""} ${r.user?.last_name || ""}`
            .trim()
            .toLowerCase();
          return (
            (r.account_name || "").toLowerCase().includes(q) ||
            name.includes(q) ||
            (r.user?.email || "").toLowerCase().includes(q)
          );
        });
      }
      accessTotalCount.value = rows.length;
      const totalPages = Math.max(1, Math.ceil(rows.length / accessPageSize));
      const safePage = Math.min(Math.max(1, page), totalPages);
      accessCurrentPage.value = safePage;
      const start = (safePage - 1) * accessPageSize;
      users.value = rows.slice(start, start + accessPageSize).map(mapUserRow);
      return;
    }

    const params = {
      page,
      size: accessPageSize,
      search,
    };
    const { data } = await getListofUser(aid, params);
    const normalized = normalizeAccessListPayload(data);
    accessTotalCount.value = normalized.count;
    accessCurrentPage.value = page;
    users.value = normalized.results.map(mapUserRow);
  } catch (error) {
    console.error("Failed to fetch users:", error);
    users.value = [];
    accessTotalCount.value = 0;
  } finally {
    accessLoading.value = false;
  }
};

const onAccessTableRequest = ({ page }) => {
  if (page != null && page > 0) {
    loadUsers(page);
  }
};

const onAccessSearch = (text) => {
  viewState.state.filters.searchText = text ?? "";
  if (accessSearchDebounceTimer) {
    clearTimeout(accessSearchDebounceTimer);
  }
  accessSearchDebounceTimer = setTimeout(() => {
    accessCurrentPage.value = 1;
    loadUsers(1);
  }, 350);
};

const sortedUsers = computed(() => {
  let usersList = users?.value ? [...users.value] : [];

  // Access Status Order
  const getAccessStatus = (user) => {
    const state = user.user?.state;
    if (state === "invited") return "Invited";
    if (state === "closed" || state === "blocked") {
      return "Closed";
    }
    if (user.is_suspended) return "Suspended";
    if (user.has_access) return "Has Access";
    if (!user.has_access) return "Blocked";
    return "Closed";
  };

  const accessOrder = {
    Invited: 0, // Invited should be first
    "Has Access": 1, // Then Has Access
    Suspended: 2, // Followed by Suspended
    Blocked: 3, // Then Blocked
    Closed: 4, // Closed should be at the end
  };

  usersList.sort((a, b) => {
    // Sort by Access Status
    const statusA = accessOrder[getAccessStatus(a)] ?? 5;
    const statusB = accessOrder[getAccessStatus(b)] ?? 5;

    if (statusA !== statusB) {
      return statusA - statusB;
    }

    // Sort by Account Name if status is equal
    const accountNameA = a?.account_name?.toLowerCase() || "";
    const accountNameB = b?.account_name?.toLowerCase() || "";

    if (accountNameA < accountNameB) return -1;
    if (accountNameA > accountNameB) return 1;

    // Fallback to sorting by user names
    const firstNameA = a?.user?.first_name?.toLowerCase() || "";
    const firstNameB = b?.user?.first_name?.toLowerCase() || "";
    const lastNameA = a?.user?.last_name?.toLowerCase() || "";
    const lastNameB = b?.user?.last_name?.toLowerCase() || "";

    if (firstNameA < firstNameB) return -1;
    if (firstNameA > firstNameB) return 1;
    if (lastNameA < lastNameB) return -1;
    if (lastNameA > lastNameB) return 1;

    return 0;
  });

  return usersList;
});

const transferOwner = async (event) => {
  proxy.$loader.show();

  try {
    const obj = {
      email: event.user.email,
    };

    await transferOwnerShip(accountIdForApi.value, obj);

    await loadUsers(accessCurrentPage.value);
    await populateTransferItems();
    proxy.$notifications.success("Ownership transferred successfully.");
  } catch (error) {
    console.error("Failed to transfer ownership:", error);
    proxy.$notifications?.error("Failed to transfer ownership. Please try again.");
  } finally {
    proxy.$loader.hide();
    viewState.state.showOnwerShipDialog.show = false;
  }
};

const populateTransferItems = async () => {
  items.value = [];
  try {
    const { data } = await getListofUser(accountIdForApi.value, {
      page: 1,
      size: 500,
    });
    const allRows = data.results || [];
    allRows.forEach((element) => {
      if (element.user.state !== "closed" && element.user.state !== "blocked") {
        if (element.is_owner === false && element.is_suspended === false) {
          items.value.push({
            id: element.user.id,
            fullName: `${element.user.first_name} ${element.user.last_name}`,
            email: element.user.email,
          });
        }
      }
    });
  } catch (e) {
    console.error("Failed to load transfer candidates:", e);
  }
};

// Watch for when the ownership transfer modal is opened
watch(
  () => viewState.state.showOnwerShipDialog.show,
  (newValue) => {
    if (newValue) {
      // When modal opens, populate the transfer items
      populateTransferItems();
    }
  }
);

onMounted(async () => {
  // ER-12719: do not require login payload `is_superuser` (may be omitted).
  isSysadmin.value = isAdminSysadmin();

  if (proxy.$getFlag) {
    impersonationFeatureEnabled.value = await proxy.$getFlag(
      LD_FLAG_IDEA_MDP_276_IMPERSONATION,
      false
    );
    adoptionEnabled.value = await proxy.$getFlag(LD_FLAG_IDEA_MDP_627, false);
  }

  const stored = localStorage.getItem("selectedAccount");
  if (stored) {
    selectedAccount.value = JSON.parse(stored);
  }

  proxy.$loader.show();

  try {
    await loadUsers(1);
  } catch (error) {
    console.error("Failed to fetch users:", error);
  } finally {
    proxy.$loader.hide();
  }
});

watch(
  () => proxy.$route.fullPath,
  (newPath, oldPath) => {
    if (oldPath === undefined) return;
    if (proxy.$route.name !== "AccountView" && proxy.$route.name !== "UserAccountAccessView") {
      return;
    }
    accessCurrentPage.value = 1;
    viewState.state.filters.searchText = "";
    loadUsers(1);
  }
);

// User action methods
const resendInviteAction = async () => {
  proxy.$loader.show();

  try {
    if (currentSelectedRow.value?.user?.id) {
      const obj = {
        user: currentSelectedRow.value.user.id,
        account: effectiveAccountIdForActions.value,
      };

      await userResendInvite(currentSelectedRow.value.user.id, obj);

      // Reload users after successful action
      await reloadUsers();

      proxy.$notifications?.success("Invite resent successfully");
    }
  } catch (error) {
    console.error("Failed to resend invite:", error);
    proxy.$notifications?.error("Failed to resend invite. Please try again.");
  } finally {
    proxy.$loader.hide();
    userActions.value.showResendInvitePopup = false;
  }
};

const suspendAccessAction = async () => {
  proxy.$loader.show();

  try {
    if (currentSelectedRow.value?.user?.id) {
      const obj = {
        email: currentSelectedRow.value.user.email,
        state: "suspended",
      };

      await userStateUpdateSuspend(
        effectiveAccountIdForActions.value,
        currentSelectedRow.value.id,
        obj
      );

      // Reload users after successful action
      await reloadUsers();

      proxy.$notifications?.success("User access suspended successfully");
    }
  } catch (error) {
    console.error("Failed to suspend access:", error);
    if (error.response?.data?.detail) {
      proxy.$notifications?.error(error.response.data.detail);
    } else {
      proxy.$notifications?.error("Failed to suspend access. Please try again.");
    }
  } finally {
    proxy.$loader.hide();
    userActions.value.showSuspendPopup = false;
  }
};

const giveBackAccessAction = async () => {
  proxy.$loader.show();

  try {
    if (currentSelectedRow.value?.user?.id) {
      const obj = {
        email: currentSelectedRow.value.user.email,
        state: "invited",
      };

      await userStateUpdateGiveBack(
        effectiveAccountIdForActions.value,
        currentSelectedRow.value.id,
        obj
      );

      // Reload users after successful action
      await reloadUsers();

      proxy.$notifications?.success("User access restored successfully");
    }
  } catch (error) {
    console.error("Failed to give back access:", error);
    if (error.response?.data?.detail) {
      proxy.$notifications?.error(error.response.data.detail);
    } else {
      proxy.$notifications?.error("Failed to restore access. Please try again.");
    }
  } finally {
    proxy.$loader.hide();
    userActions.value.showGiveBackPopup = false;
  }
};

const reloadUsers = async () => {
  await loadUsers(accessCurrentPage.value);
};

// User action dialog handlers
const onActionUserConfirmationDialog = () => {
  if (userActions.value.showResendInvitePopup) {
    resendInviteAction();
  } else if (userActions.value.showSuspendPopup) {
    suspendAccessAction();
  } else if (userActions.value.showGiveBackPopup) {
    giveBackAccessAction();
  } else if (userActions.value.showImpersonatePopup) {
    confirmImpersonationAction();
  }
};

const onCloseUserConfirmationDialog = () => {
  userActions.value.showResendInvitePopup = false;
  userActions.value.showSuspendPopup = false;
  userActions.value.showGiveBackPopup = false;
  userActions.value.showImpersonatePopup = false;
};

const confirmImpersonationAction = async () => {
  if (!impersonationFeatureEnabled.value) {
    proxy.$notifications?.error?.("Impersonation is not available.");
    onCloseUserConfirmationDialog();
    return;
  }

  const row = currentSelectedRow.value;
  // Impersonation is scoped by Access, not User (rules page §1/§9.1) — the backend
  // start endpoint expects `access_id`. currentSelectedRow.id is the Access id in
  // both the account view and the user drill-down view.
  const accessId = row?.id;
  if (!accessId) {
    proxy.$notifications?.error?.("Unable to impersonate: user not found.");
    onCloseUserConfirmationDialog();
    return;
  }

  try {
    proxy.$loader?.show?.();

    const { data } = await startImpersonation({ access_id: accessId });

    const exchangeCode = data.exchange_code || data.handoff_token;
    if (!exchangeCode) {
      throw new Error("Impersonation handoff token missing from server response.");
    }

    // Hand the return path to the account app via the URL. The admin session stays on
    // the admin origin — no admin token is carried into the impersonated session.
    const accountAppUrl = resolveAccountAppUrl(configService);
    const handoffCode = encodeURIComponent(exchangeCode);
    const returnPath = encodeURIComponent(proxy.$route.fullPath);
    window.location.href = `${accountAppUrl}/impersonation/handoff?code=${handoffCode}&return=${returnPath}`;
  } catch (error) {
    console.error("Failed to start impersonation:", error);
    // DRF surfaces validation errors (e.g. "session already active") as a top-level
    // array or string, so check those before the generic fallback.
    const data = error.response?.data;
    const message =
      (Array.isArray(data) ? data[0] : null) ||
      data?.detail ||
      data?.error ||
      (typeof data === "string" ? data : null) ||
      "Failed to start impersonation. Please try again.";
    proxy.$notifications?.error?.(message);
  } finally {
    proxy.$loader?.hide?.();
    onCloseUserConfirmationDialog();
  }
};

// Custom dropdown handler to intercept operations before they reach viewState
const handleDropdownOperation = (operation, item) => {
  currentSelectedRow.value = sortedUsers.value.find((user) => user.id === item.id);

  if (operation.value === "resend_invite") {
    userActions.value.showResendInvitePopup = true;
  } else if (operation.value === "suspend") {
    userActions.value.showSuspendPopup = true;
  } else if (operation.value === "give_back_access") {
    userActions.value.showGiveBackPopup = true;
  } else if (operation.value === "impersonate" && impersonationFeatureEnabled.value) {
    userActions.value.showImpersonatePopup = true;
  } else {
    // For other operations like transfer ownership, delegate to viewState
    viewState.handleAccountUserDropdownOperation(operation);
  }
};

// Override the table config to use our custom dropdown handler
const customTableConfig = computed(() => {
  const originalConfig = tableConfig.tableParams.value;
  const ACCOUNT_COLUMN_INDEX = 1;

  let headers = originalConfig.headers;
  let columnsBase = originalConfig.columns;

  if (isUserDrilldown.value) {
    // Profile header already shows the user; drop the User column.
    headers = originalConfig.headers.slice(1);
    columnsBase = originalConfig.columns.slice(1);
  } else {
    // Account detail: drop Account column (same account for every row as page context).
    headers = originalConfig.headers.filter((_, i) => i !== ACCOUNT_COLUMN_INDEX);
    columnsBase = originalConfig.columns.filter((_, i) => i !== ACCOUNT_COLUMN_INDEX);
  }

  const modifiedColumns = columnsBase.map((column) => {
    if (column.component === "Dropdown") {
      return {
        ...column,
        events: (item) => ({
          onClick: (operation) => {
            handleDropdownOperation(operation, item);
          },
        }),
      };
    }
    return column;
  });

  return {
    ...originalConfig,
    headers,
    columns: modifiedColumns,
  };
});
</script>

<style scoped>
.icon-container {
  cursor: pointer;
}

::v-deep(.table-content) {
  border: none !important;
}

.account-header {
  margin-top: 20px;
  padding: 12px 16px 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.account-header-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  margin-top: 2px;
}

.account-header-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.account-header-title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 12px;
}

.account-header-name {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  color: #101828;
  margin: 0;
}

.account-header-email {
  font-size: 13px;
  line-height: 1.4;
  margin: 0 !important;
  color: #667085;
}

/* Status badge: same row as name; padding applies to badge root (badge-container) */
.account-header ::v-deep(.account-header-status.badge-container) {
  padding: 4px 12px 4px 10px !important;
  gap: 6px;
  border-radius: 999px;
  flex-shrink: 0;
}

.account-header ::v-deep(.account-header-status .account-status-dot.custom-badge) {
  margin: 0 !important;
  align-self: center;
}

.account-header ::v-deep(.account-header-status .status-label) {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.25;
}

.account-header ::v-deep(.account-header-status.storybook-account-status-badge--operational) {
  border-color: #86efac;
  background: linear-gradient(180deg, #f0fdf4 0%, #ecfdf3 100%);
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.account-header
  ::v-deep(.account-header-status.storybook-account-status-badge--operational .status-label) {
  color: #047857;
}

.account-header p {
  margin-bottom: 0px !important;
}

::v-deep(.menu-tabs) {
  padding: 10px !important;
  margin-right: 10px !important;
}

::v-deep(.storybook-status-badge .v-badge__badge) {
  position: absolute !important;
  top: -7px !important;
}

::v-deep .custom-assign .allocation_icon > .content {
  top: 45%;
}
</style>
